/**
 * SendStatementsOptionsModal
 *
 * Step 1 of the 3-step Send Statements flow.
 *
 * Allows the user to configure statement parameters before proceeding to
 * the Send Email compose step (Step 2 – BulkEmailModal / CustomerSendEmailModal).
 *
 * Fields:
 *  - Statement Date        (defaults to today, editable)
 *  - Currency Values       (Customer Currency | Base Currency)
 *  - Exclude Disputed      (toggle, default off)
 *  - Exclude Credit Balance Accounts (toggle, default off)
 *  - Exclude Credit Transactions     (toggle, default off)
 *
 * The statement's closing text is taken as-is from the selected template.
 *
 * Buttons: Preview | Cancel | Next
 *
 * Guards:
 *  - If no statement template exists for the company, Next is disabled with
 *    a blocking message directing the user to Settings > Templates.
 *  - If every selected customer is excluded as a credit-balance account,
 *    Preview and Next are disabled with an informational message.
 */

import { pdf } from '@react-pdf/renderer';
import { ArrowRight, Eye, FileText, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useCompany } from '@/contexts/CompanyContext';
import { useApi } from '@/hooks/useApi';
import { useCompanyWithProfile } from '@/hooks/useCompanyWithProfile';
import { useStatementTemplatesByIds, useTemplatesList } from '@/hooks/useTemplatesData';
import { resolveCompanyBankDetails } from '@/lib/company-bank-details';
import {
  fetchAgeingBandsConfig,
  fetchAgeingBasis,
  fetchCustomerAgeing,
  fetchLatestReceipt,
  fetchOutstandingTransactions,
  resolveStatementLogoUrl,
} from '@/lib/fetch-email-transactions';
import { StatementDocument } from '@/lib/react-pdf/StatementDocument';
import { partitionStatementRecipients } from '@/lib/statement-recipients';
import { findTemplateByStoredId } from '@/lib/template-identity';
import {
  buildCustomerPreviewData,
  buildSampleData,
  type CustomerPreviewInput,
  reconcileBuckets,
  resolveStatementCurrency,
} from '@/lib/react-pdf/statement-data';
import type { Template } from '@/types/template';
import type { TemplateBuilderConfig } from '@/types/template-builder';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A selected customer plus its persisted statement-template selection. */
export type StatementSelectionCustomer = CustomerPreviewInput & {
  id: string;
  /** Assigned statement template id (undefined = no template, send falls back to company default) */
  statementTemplateId?: string;
};

export interface SendStatementOptions {
  statementDate: Date;
  currencyMode: 'customer' | 'base';
  excludeDisputed: boolean;
  /** Skip customers whose statement balance is zero or in credit */
  excludeCreditBalanceAccounts: boolean;
  /** Omit credit notes, credit adjustments and unallocated receipts from the statement */
  excludeCreditTransactions: boolean;
  /** Customer ids left after the credit-balance exclusion — the effective recipients */
  eligibleCustomerIds: string[];
  /** How many selected customers the credit-balance exclusion removed */
  excludedCreditBalanceCount: number;
  /** Company default statement template summary (fallback when a customer has no assigned template) */
  companyDefaultTemplate: Template | null;
  /** Full templates (incl. pdfSettings) keyed by template id, for every template used across the selection */
  fullTemplatesById: Map<string, Template>;
  /** Effective template id resolved per customer id (assigned template, else company default) */
  effectiveTemplateIdByCustomerId: Map<string, string>;
}

export interface SendStatementsOptionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All customers selected in the grid */
  selectedCustomers: StatementSelectionCustomer[];
  /** Called with the configured options when user clicks Next */
  onNext: (options: SendStatementOptions) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SendStatementsOptionsModal({
  open,
  onOpenChange,
  selectedCustomers,
  onNext,
}: Readonly<SendStatementsOptionsModalProps>) {
  const selectedCount = selectedCustomers.length;
  // ── State ─────────────────────────────────────────────────────────────────
  const [statementDate, setStatementDate] = useState<Date>(new Date());
  const [currencyMode, setCurrencyMode] = useState<'customer' | 'base'>('customer');
  const [excludeDisputed, setExcludeDisputed] = useState(false);
  const [excludeCreditBalanceAccounts, setExcludeCreditBalanceAccounts] = useState(false);
  const [excludeCreditTransactions, setExcludeCreditTransactions] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);

  // ── Effective recipients ──────────────────────────────────────────────────
  const { eligible: eligibleCustomers, excluded: excludedCustomers } = useMemo(
    () => partitionStatementRecipients(selectedCustomers, excludeCreditBalanceAccounts, currencyMode),
    [selectedCustomers, excludeCreditBalanceAccounts, currencyMode],
  );
  const eligibleCount = eligibleCustomers.length;
  const excludedCount = excludedCustomers.length;
  const allCustomersExcluded = selectedCount > 0 && eligibleCount === 0;
  const customerCountLabel =
    excludedCount > 0
      ? `${eligibleCount} of ${selectedCount} customers selected`
      : `${eligibleCount} ${eligibleCount === 1 ? 'customer' : 'customers'} selected`;
  const previewCustomer = eligibleCustomers[0] ?? null;

  // ── Company ───────────────────────────────────────────────────────────────
  const { selectedCompany } = useCompany();
  const { apiBaseUrl, getHeaders } = useApi();
  const { data: companyWithProfile } = useCompanyWithProfile(selectedCompany?.id);
  const companyLogoUrl = companyWithProfile?.details?.logoUrl ?? selectedCompany?.logoUrl ?? null;

  // ── Templates ─────────────────────────────────────────────────────────────
  const { data: allTemplates = [], isLoading: templatesLoading } = useTemplatesList();
  const statementTemplates = useMemo(() => allTemplates.filter((t) => t.type === 'statement'), [allTemplates]);
  const companyDefaultTemplate = useMemo(
    () => statementTemplates.find((t) => t.isDefault) ?? null,
    [statementTemplates],
  );
  const hasAnyStatementTemplate = statementTemplates.length > 0;

  // Resolve the effective template id per customer: the customer's assigned template when set,
  // otherwise the company default. Customers set to "None" have no assigned id and therefore also
  // fall back to the company default.
  const effectiveTemplateIdByCustomerId = useMemo(() => {
    const map = new Map<string, string>();
    for (const customer of selectedCustomers) {
      // The customer stores the group-level id, so the row that applies here may be this company's
      // override of it; its own id is what fetches the content actually sent.
      const assigned = findTemplateByStoredId(statementTemplates, customer.statementTemplateId);
      const effectiveId = assigned?.id ?? companyDefaultTemplate?.id;
      if (effectiveId) map.set(customer.id, effectiveId);
    }
    return map;
  }, [selectedCustomers, statementTemplates, companyDefaultTemplate]);

  const neededTemplateIds = useMemo(() => {
    const ids = [...effectiveTemplateIdByCustomerId.values()];
    if (companyDefaultTemplate?.id) ids.push(companyDefaultTemplate.id);
    return ids;
  }, [effectiveTemplateIdByCustomerId, companyDefaultTemplate]);

  // Fetch full detail (includes pdfSettings) for every template used across the selection
  const { templatesById: fullTemplatesById, isLoading: templateDetailLoading } =
    useStatementTemplatesByIds(neededTemplateIds);

  // Every selected customer must resolve to a usable template, otherwise Send is blocked
  const customersWithoutTemplate = eligibleCustomers.filter((c) => !effectiveTemplateIdByCustomerId.has(c.id));
  const allCustomersServable = eligibleCount > 0 && customersWithoutTemplate.length === 0;

  const previewEffectiveId = previewCustomer ? effectiveTemplateIdByCustomerId.get(previewCustomer.id) : undefined;
  const previewFullTemplate = previewEffectiveId ? (fullTemplatesById.get(previewEffectiveId) ?? null) : null;

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setStatementDate(new Date());
      setCurrencyMode('customer');
      setExcludeDisputed(false);
      setExcludeCreditBalanceAccounts(false);
      setExcludeCreditTransactions(false);
      setIsPreviewing(false);
    }
  }, [open]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCancelClick = () => {
    onOpenChange(false);
  };

  const handlePreview = async () => {
    if (!previewFullTemplate) return;
    const ps = previewFullTemplate.pdfSettings;
    if (!ps?.layoutSettings || !ps.contentSettings || !ps.fieldsSettings) {
      toast.error('Template is missing PDF settings. Please re-save it in the template builder.');
      return;
    }
    setIsPreviewing(true);
    try {
      const { logoUrl: resolvedLogoUrl, logoSource } = await resolveStatementLogoUrl({
        pdfSettings: ps,
        companyLogoUrl,
        apiBaseUrl,
        companyId: selectedCompany?.id,
        getHeaders,
      });

      const apiConfig = { apiBaseUrl, getHeaders };
      const previewAgeingCurrency = previewCustomer
        ? resolveStatementCurrency(previewCustomer, currencyMode)
        : undefined;
      const bankDetails = resolveCompanyBankDetails(companyWithProfile?.bankDetails, previewAgeingCurrency);
      const [previewTransactions, previewAgeing, previewReceipt, previewAgeingBands, previewAgeingBasis] =
        previewCustomer?.id
          ? await Promise.all([
              fetchOutstandingTransactions(apiConfig, previewCustomer.id, excludeDisputed),
              // The server bands net credits in, so they are not fetched when credits are excluded
              excludeCreditTransactions
                ? null
                : fetchCustomerAgeing(apiConfig, previewCustomer.id, previewAgeingCurrency),
              fetchLatestReceipt(apiConfig, previewCustomer.id),
              fetchAgeingBandsConfig(apiConfig),
              excludeCreditTransactions ? fetchAgeingBasis(apiConfig) : undefined,
            ])
          : [[], null, null, undefined, undefined];

      const config: TemplateBuilderConfig = {
        layoutSettings: ps.layoutSettings,
        contentSettings: ps.contentSettings,
        fieldsSettings: previewAgeingBands
          ? {
              ...ps.fieldsSettings,
              agingSummary: {
                ...ps.fieldsSettings.agingSummary,
                buckets: reconcileBuckets(ps.fieldsSettings.agingSummary.buckets, previewAgeingBands),
              },
            }
          : ps.fieldsSettings,
        primaryColor: ps.styling?.primaryColor ?? '#1a5c5c',
        documentFont: ps.styling?.fontFamily ?? 'Helvetica',
        logoUrl: resolvedLogoUrl,
        logoSize: ps.logoSize ?? 'medium',
        logoSource,
        companyName: selectedCompany?.name ?? ps.companyName ?? '',
        companyEmail: selectedCompany?.email ?? companyWithProfile?.details?.email ?? undefined,
        companyPhone: selectedCompany?.phone ?? companyWithProfile?.details?.phone ?? undefined,
        companyReg:
          selectedCompany?.companyRegistrationNumber ??
          companyWithProfile?.details?.companyRegistrationNumber ??
          undefined,
        companyVat: selectedCompany?.vatRegistrationNumber ?? companyWithProfile?.details?.vatNumber ?? undefined,
        companyAddress: companyWithProfile?.details?.addressLine ?? undefined,
        companyCountry: companyWithProfile?.details?.country ?? undefined,
        companyBankName: bankDetails?.accountName ?? undefined,
        sortCode: bankDetails?.sortCode ?? undefined,
        bankAccountNumber: bankDetails?.accountNumber ?? undefined,
        ibanNumber: bankDetails?.ibanNumber ?? undefined,
        swiftCode: bankDetails?.swiftBicCode ?? undefined,
      };
      const enrichedPreviewCustomer = previewCustomer
        ? {
            ...previewCustomer,
            lastPaymentAmount: previewReceipt?.amount,
            lastPaymentCurrencyAmount: previewReceipt?.currencyAmount,
          }
        : undefined;
      const data = enrichedPreviewCustomer
        ? buildCustomerPreviewData(
            config,
            enrichedPreviewCustomer,
            previewTransactions,
            previewAgeing ?? null,
            statementDate,
            currencyMode,
            previewAgeingBands,
            { excludeCreditTransactions, ageingBasis: previewAgeingBasis },
          )
        : buildSampleData(config, previewAgeingBands);
      const blob = await pdf(<StatementDocument config={config} data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Failed to generate statement preview.');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleNext = () => {
    if (!allCustomersServable) return;
    onNext({
      statementDate,
      currencyMode,
      excludeDisputed,
      excludeCreditBalanceAccounts,
      excludeCreditTransactions,
      eligibleCustomerIds: eligibleCustomers.map((c) => c.id),
      excludedCreditBalanceCount: excludedCount,
      companyDefaultTemplate,
      fullTemplatesById,
      effectiveTemplateIdByCustomerId,
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle className="text-lg font-semibold">Send Statements</DialogTitle>
              <DialogDescription className="mt-1">Step 1 of 4 — Configure statement options</DialogDescription>
            </div>
            {/* Customer count indicator */}
            <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1.5 text-sm shrink-0">
              <Users className="h-3.5 w-3.5" />
              {customerCountLabel}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* No statement template warning */}
          {!templatesLoading && !hasAnyStatementTemplate && (
            <Alert variant="destructive">
              <AlertDescription>
                No statement template has been set up for this company. Please go to{' '}
                <strong>Settings &gt; Templates</strong> to create a statement template before sending statements.
              </AlertDescription>
            </Alert>
          )}

          {/* Every selected customer is a credit-balance account */}
          {allCustomersExcluded && (
            <Alert>
              <AlertDescription>
                All {selectedCount} selected {selectedCount === 1 ? 'customer has' : 'customers have'} a zero or credit
                outstanding balance, so there are no statements to send. Turn off{' '}
                <strong>Exclude Credit Balance Accounts</strong> or change your selection.
              </AlertDescription>
            </Alert>
          )}

          {/* Some eligible customers have no usable template and no company default exists */}
          {!templatesLoading && hasAnyStatementTemplate && !allCustomersExcluded && !allCustomersServable && (
            <Alert variant="destructive">
              <AlertDescription>
                {customersWithoutTemplate.length} of {eligibleCount} selected{' '}
                {customersWithoutTemplate.length === 1 ? 'customer has' : 'customers have'} no assigned statement
                template and no company default is set. Assign a template to{' '}
                {customersWithoutTemplate.length === 1 ? 'that customer' : 'those customers'} or set a default in{' '}
                <strong>Settings &gt; Templates</strong> before sending statements.
              </AlertDescription>
            </Alert>
          )}

          {/* Statement template indicator */}
          {allCustomersServable && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4 shrink-0 text-primary" />
              <span>
                Each statement uses the customer's assigned template
                {companyDefaultTemplate ? (
                  <>
                    , falling back to the company default:{' '}
                    <span className="font-medium text-foreground">{companyDefaultTemplate.name}</span>
                  </>
                ) : (
                  '.'
                )}
              </span>
            </div>
          )}

          {/* Statement Date */}
          <div className="space-y-1.5">
            <Label htmlFor="statement-date">Statement Date</Label>
            <DatePicker
              date={statementDate}
              onDateChange={(d) => setStatementDate(d ?? new Date())}
              placeholder="Select statement date"
              dateFormat="dd/MM/yyyy"
            />
          </div>

          {/* Currency Values */}
          <div className="space-y-1.5">
            <Label htmlFor="currency-values">Currency Values</Label>
            <Select
              value={currencyMode}
              onValueChange={(v) => {
                if (v === 'customer' || v === 'base') {
                  setCurrencyMode(v);
                }
              }}
            >
              <SelectTrigger id="currency-values" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer Currency</SelectItem>
                <SelectItem value="base">Base Currency</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Exclude Disputed Transactions */}
          <div className="flex items-center justify-between rounded-lg border px-4 py-3">
            <div className="space-y-0.5">
              <Label htmlFor="exclude-disputed" className="text-sm font-medium cursor-pointer">
                Exclude Disputed Transactions
              </Label>
              <p className="text-xs text-muted-foreground">Disputed transactions will be omitted from the statement</p>
            </div>
            <Switch id="exclude-disputed" checked={excludeDisputed} onCheckedChange={setExcludeDisputed} />
          </div>

          {/* Exclude Credit Balance Accounts */}
          <div className="flex items-center justify-between rounded-lg border px-4 py-3">
            <div className="space-y-0.5">
              <Label htmlFor="exclude-credit-balances" className="text-sm font-medium cursor-pointer">
                Exclude Credit Balance Accounts
              </Label>
              <p className="text-xs text-muted-foreground">
                Customers with a zero or credit outstanding balance will not receive a statement
              </p>
              {excludeCreditBalanceAccounts && excludedCount > 0 && (
                <p className="text-xs font-medium text-foreground">
                  {excludedCount} of {selectedCount} {excludedCount === 1 ? 'customer' : 'customers'} excluded
                </p>
              )}
            </div>
            <Switch
              id="exclude-credit-balances"
              checked={excludeCreditBalanceAccounts}
              onCheckedChange={setExcludeCreditBalanceAccounts}
            />
          </div>

          {/* Exclude Credit Transactions */}
          <div className="flex items-center justify-between rounded-lg border px-4 py-3">
            <div className="space-y-0.5">
              <Label htmlFor="exclude-credit-transactions" className="text-sm font-medium cursor-pointer">
                Exclude Credit Transactions
              </Label>
              <p className="text-xs text-muted-foreground">
                Credit notes, credit adjustments and unallocated receipts will be omitted from the statement and ageing
                summary
              </p>
            </div>
            <Switch
              id="exclude-credit-transactions"
              checked={excludeCreditTransactions}
              onCheckedChange={setExcludeCreditTransactions}
            />
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          {/* Left side: Preview */}
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={!previewFullTemplate || allCustomersExcluded || templateDetailLoading || isPreviewing}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            {isPreviewing ? 'Generating…' : 'Preview'}
          </Button>

          {/* Right side: Cancel + Next */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button
              onClick={handleNext}
              disabled={!allCustomersServable || templatesLoading || templateDetailLoading}
              className="gap-2"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
